import TextInput from '@Common/Components/Input/TextInput.vue';
import SelectInput from '@Common/Components/Input/SelectInput.vue';
import MultiSelectInput from '@Common/Components/Input/MultiSelectInput.vue';
import CheckboxInput from '@Common/Components/Input/CheckboxInput.vue';
import RadioInput from '@Common/Components/Input/RadioInput.vue';
import NumberInput from '@Common/Components/Input/NumberInput.vue';
import FieldValue from '@Common/Components/Input/FieldValue.vue';
import {
    CheckboxFieldConfig,
    CommunityFieldConfig,
    CommunityOrder,
    CommunitySelectionType,
    DateFieldConfig,
    DigitalFieldConfig,
    FieldConfig,
    FieldType,
    MultiCheckboxFieldConfig, ScalarFieldValue, SectionConfig,
    SelectFieldConfig,
    TextFieldConfig,
    TextZoneFieldConfig,
    FormConfig, FormFieldValue, InputFieldValue, inputTypeMapping,
    FileFieldConfig,
    UploadFieldConfig, ChildrenCompaniesFieldConfig
} from '@Common/Components/Form/types';
import {computed, Ref, ComputedRef} from "vue";
import RichTextInput from '../Components/Input/RichTextInput.vue';
import TextareaInput from '../Components/Input/TextareaInput.vue';
import DatePickerInput from '../Components/Input/DatePickerInput.vue';
import FileInput from '../Components/Input/FileInput.vue';
import {lookup} from 'mime-types';
import {HelpPosition} from "@Components/Input/InputDecorator.vue";

export type InputFormValues = {
    [key: string]: ScalarFieldValue | InputFieldValue[];
    customFields: InputFieldValue[];
};

export const useFieldsMapping = () => {
    const inputComponent = computed(() => (field: FieldConfig, readOnly?: boolean) => {
        if (field.readOnly || readOnly) {
            return FieldValue;
        }

        switch (field.type) {
            case FieldType.Digital:
                return NumberInput;

            case FieldType.Checkbox:
            case FieldType.OptIn:
                return CheckboxInput;

            case FieldType.Radio:
                return RadioInput;
            case FieldType.Date:
                return DatePickerInput;

            case FieldType.Select:
            case FieldType.Country:
            case FieldType.State:
            case FieldType.ChildrenCompanies:
            case FieldType.FormId:
                return SelectInput;

            case FieldType.MultiCheckbox:
                return MultiSelectInput;

            case FieldType.Community:
                if ((field as CommunityFieldConfig).selectionType === CommunitySelectionType.Single) {
                    return SelectInput;
                }
                return MultiSelectInput;

            case FieldType.TextZone:
                if ((field as TextZoneFieldConfig).html) {
                    return RichTextInput;
                }
                return TextareaInput;

            case FieldType.File:
            case FieldType.Upload:
                return FileInput;
            case FieldType.Text:
            case FieldType.Email:
            case FieldType.Url:
            case FieldType.Phone:
                return TextInput;

            default:
                return null;
        }
    });

    const genericProps = (field: FieldConfig) => ({
        id: String(field.id),
        name: field.name,
        label: field.label,
        required: field.required,
        help: field.description,
        helpPosition: field.descriptionPosition
    });

    const digitalProps = (field: DigitalFieldConfig) => ({
        ...genericProps(field),
        min: (field.allowNegative) ? undefined : 0,
        max: field.digitalLimit || undefined,
        step: (field.allowDecimals) ? 0.01 : 1
    });

    const dateProps = (field: DateFieldConfig) => ({
        ...genericProps(field),
        minDate: field.minDate ? new Date(field.minDate) : undefined,
        maxDate: field.maxDate ? new Date(field.maxDate) : undefined,
        displayMode: field.displayMode,
        defaultValue: new Date(field.defaultValue) || null,
        clearable: !field.required
    });

    const checkboxProps = (field: CheckboxFieldConfig) => ({
        ...genericProps(field),
        label: field.label,
        valueLabel: field.option
    });

    const optInProps = (field: CheckboxFieldConfig) => {
        if (doesHelpRequireLabel(field.description || '', field.descriptionPosition || HelpPosition.None)) {
            return {...checkboxProps(field), label: field.label, valueLabel: field.option};
        }
        return {...checkboxProps(field), label: undefined, valueLabel: field.label};
    };

    const selectProps = (field: SelectFieldConfig) => {
        const options = field.type === FieldType.Select && field.sort
            ? [...field.options].sort((a, b) => a.label.localeCompare(b.label))
            : field.options;
        return {
            ...genericProps(field),
            options: options.map(option => ({value: option.value, label: option.label}))
        }
    };

    const childrenCompaniesProps = (field: ChildrenCompaniesFieldConfig) => {
        const options = field.sort
            ? [...field.options].sort((a, b) => a.label.localeCompare(b.label))
            : field.options;
        return {
            ...genericProps(field),
            options: options.map(option => ({value: option.stringValue, label: option.label}))
        }
    }

    const multiSelectProps = (field: MultiCheckboxFieldConfig) => ({
        ...genericProps(field),
        options: field.options,
        mode: 'tags',
        groupSelect: false,
        closeOnSelect: false,
        hideSelected: false,
        searchable: field.isChosen
    });

    const communityProps = (field: CommunityFieldConfig) => {
        const options = [...field.options].sort((a, b) =>
            field.order === CommunityOrder.Descendant ? b.label.localeCompare(a.label) : a.label.localeCompare(b.label)
        );

        if (field.selectionType === CommunitySelectionType.Single) {
            return {
                ...genericProps(field),
                options: options.map(option => ({value: option.value, label: option.label}))
            }
        }

        return {
            ...genericProps(field),
            options: options.map(option => ({value: option.value, label: option.label})),
            mode: 'tags',
            groupSelect: false,
            closeOnSelect: false,
            hideSelected: false,
            searchable: true,
        }
    };

    const textProps = (field: TextFieldConfig) => ({
        ...genericProps(field),
        pattern: field.pattern,
        maxlength: field.charLimit
    });

    const textZoneProps = (field: TextZoneFieldConfig) => ({
        ...genericProps(field),
        maxlength: field.charLimit || undefined,
        rows: field.nbLineToDisplay
    });

    const extensionsToMimeTypes = (extensions: string) => {
        return extensions.split(',').map((ext: string) => {
            const mimeType = lookup(ext);
            return mimeType || '';
        });
    };

    const fileProps = (field: FileFieldConfig) => ({
        ...genericProps(field),
        accept: extensionsToMimeTypes(field.typesAllowed) || undefined,
        maxFiles: field.multiple ? undefined : 1,
        fieldId: field.id,
        outputFormat: (field.multiple) ? 'array' : 'string'
    });

    const uploadProps = (field: UploadFieldConfig) => ({
        ...genericProps(field),
        fieldId: field.id,
        accept: ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml', 'image/svg', 'image/webp', 'image/tiff', 'image/bmp'],
        outputFormat: 'string'
    });

    const inputProps = computed(() => (field: FieldConfig, readOnly?: boolean) => {
        if (field.readOnly || readOnly) {
            return {...genericProps(field), field: field};
        }

        switch (field.type) {
            case FieldType.Digital:
                return digitalProps(field as DigitalFieldConfig);

            case FieldType.Date:
                return dateProps(field as DateFieldConfig);

            case FieldType.Checkbox:
                return checkboxProps(field as CheckboxFieldConfig);

            case FieldType.OptIn:
                return optInProps(field as CheckboxFieldConfig);

            case FieldType.Select:
            case FieldType.Country:
            case FieldType.State:
            case FieldType.Radio:
            case FieldType.FormId:
                return selectProps(field as SelectFieldConfig);

            case FieldType.ChildrenCompanies:
                return childrenCompaniesProps(field as ChildrenCompaniesFieldConfig);

            case FieldType.MultiCheckbox:
                return multiSelectProps(field as MultiCheckboxFieldConfig);

            case FieldType.Community:
                return communityProps(field as CommunityFieldConfig);

            case FieldType.TextZone:
                return textZoneProps(field as TextZoneFieldConfig);

            case FieldType.File:
                return fileProps(field as FileFieldConfig);

            case FieldType.Upload:
                return uploadProps(field as UploadFieldConfig);

            case FieldType.Text:
            case FieldType.Email:
            case FieldType.Url:
            case FieldType.Incremental:
            default:
                return textProps(field as TextFieldConfig);
        }
    });

    const formatValue = computed(() => (value: unknown, field: FieldConfig): boolean | unknown => {
        if (field.type === FieldType.Checkbox) {
            return Boolean(value);
        }
        return value;
    });

    const formatValues = (
        formData: ComputedRef<FormConfig | null>,
        fieldsValues: Ref<Record<string, FormFieldValue>>
    ): InputFormValues => {
        const nativeValues: { [key: string]: ScalarFieldValue | InputFieldValue[]; } = {};
        const customFields: InputFieldValue[] = [];
        formData.value?.sections?.forEach((section: SectionConfig) => {
            section.columns?.forEach((column) => {
                column?.forEach((field: FieldConfig) => {
                    if (field.readOnly || fieldsValues.value[field.name] === undefined) {
                        return;
                    }
                    if (field.isNative) {
                        nativeValues[field.name] = fieldsValues.value[field.name] as ScalarFieldValue;
                    } else {
                        const fieldValue: InputFieldValue = {name: field.name};
                        const mappingKey = Object.keys(inputTypeMapping).find(
                            key => inputTypeMapping[key].includes(field.type as FieldType)
                        );

                        if (mappingKey) {
                            fieldValue[mappingKey] = fieldsValues.value[field.name];
                            customFields.push(fieldValue);
                        }
                    }
                });
            });
        });
        return {...nativeValues, customFields};
    }

    const doesHelpRequireLabel = (text: string, position: HelpPosition) => text && position && [HelpPosition.Tooltip, HelpPosition.Label].includes(position);

    return {
        inputComponent,
        inputProps,
        formatValue,
        formatValues,
        doesHelpRequireLabel
    }
};